import { customAuth } from './customAuth';
import { EncryptionService } from './encryptionService';

import { getApiBaseUrl, isDevelopment } from '../config/constants';

// Get API base URL - centralized configuration
const API_BASE_URL = isDevelopment() ? '/api' : getApiBaseUrl();

export interface ProgressItem {
  PK: string;
  SK: string;
  program: string;
  track: string;
  step_number?: number;
  step_title?: string;
  completed?: boolean;
  started_at?: string;
  completed_at?: string;
  data?: any;
  created_at?: string;
  updated_at?: string;
}

export interface StepProgressRequest {
  program: string;
  track: string;
  step_number: number;
  step_title: string;
  completed: boolean;
  data?: any;
  started_at?: string;
  completed_at?: string;
}

export interface JourneyProgress {
  opted_out: boolean;
  journey_start?: string;
  all_steps_completed?: boolean;
  any_steps_started?: boolean;
}

export interface UserPreferences {
  date_display_opted_out: boolean;
}

class ProgressService {
  private async getHeaders(): Promise<HeadersInit> {
    const token = customAuth.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async getUserProgress(): Promise<ProgressItem[]> {
    const response = await fetch(`${API_BASE_URL}/progress`, {
      method: 'GET',
      headers: await this.getHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to get user progress: ${response.statusText}`);
    }
    const data = await response.json();
    return data.progress || [];
  }

  async getProgramProgress(program: string): Promise<ProgressItem[]> {
    const response = await fetch(`${API_BASE_URL}/progress/${encodeURIComponent(program)}`, {
      method: 'GET',
      headers: await this.getHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to get program progress: ${response.statusText}`);
    }
    const data = await response.json();
    return data.progress || [];
  }

  async createProgram(request: { program: string; program_description?: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/progress/program`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      throw new Error(`Failed to create program: ${response.statusText}`);
    }
    return response.json();
  }

  async createTrack(request: { program: string; track: string; track_description?: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/progress/track`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      throw new Error(`Failed to create track: ${response.statusText}`);
    }
    return response.json();
  }

  async initializeProgress(): Promise<void> {
    try {
      await this.createProgram({
        program: 'SAA',
        program_description: 'Sex Addicts Anonymous'
      });
    } catch (error) {
      console.log('SAA program might already exist:', error);
    }
    try {
      await this.createTrack({
        program: 'SAA',
        track: '12-steps',
        track_description: 'The 12 Steps of SAA'
      });
    } catch (error) {
      console.log('12-steps track might already exist:', error);
    }
  }

  async getStepProgress(program: string, track: string, stepNumber: number): Promise<ProgressItem | null> {
    try {
      const progress = await this.getProgramProgress(program);
      const stepKey = `${program}#${track}#step${stepNumber}`;
      const stepRecord = progress.find(item => item.SK === stepKey);
      
      if (!stepRecord) {
        return null;
      }
      
      // Handle decryption if data is encrypted
      if (stepRecord.data?.encryptedData) {
        try {
          const decryptedData = await EncryptionService.decrypt(stepRecord.data.encryptedData);
          
          // Merge decrypted data back into the step record
          return {
            ...stepRecord,
            data: {
              ...stepRecord.data,
              ...decryptedData,
              // Remove the encrypted data from the response
              encryptedData: undefined,
              encrypted: undefined
            }
          };
        } catch (decryptionError) {
          // Silently handle decryption errors (likely old data with different encryption key)
          // Return data without decryption if it fails
          return stepRecord;
        }
      }
      
      return stepRecord;
    } catch (error) {
      console.error('Error getting step progress:', error);
      return null;
    }
  }

  async getAllStepProgress(program: string, track: string): Promise<Record<number, ProgressItem | null>> {
    const progress = await this.getProgramProgress(program);
    const stepRecords = progress.filter(item => 
      item.SK.startsWith(`${program}#${track}#step`)
    );
    const result: Record<number, ProgressItem | null> = {};
    
    for (const record of stepRecords) {
      if (record.step_number) {
        let decryptedData = {};
        
        // Handle decryption if data is encrypted
        if (record.data?.encryptedData) {
          try {
            decryptedData = await EncryptionService.decrypt(record.data.encryptedData);
          } catch (decryptionError) {
            // Silently handle decryption errors (likely old data with different encryption key)
            decryptedData = {};
          }
        }
        
        const finalData = {
          ...record.data,
          ...decryptedData,
          // Keep encrypted data for individual step pages, but don't decrypt here
          encryptedData: record.data?.encryptedData,
          encrypted: record.data?.encrypted
        };
        
        // Standardize actions location - always put actions at the top level for overview page
        if (finalData.data?.actions && !finalData.actions) {
          // Promoting nested actions to top level
          finalData.actions = finalData.data.actions;
        }
        
        // Overview Debug - Step structure processed
        

        
        result[record.step_number] = {
          ...record,
          data: finalData
        };
      }
    }
    return result;
  }

  async saveStepActions(program: string, track: string, stepNumber: number, actions: any[]): Promise<any> {
    return this.updateStepProgress({
      program,
      track,
      step_number: stepNumber,
      step_title: `Step ${stepNumber}`,
      completed: false,
      data: { actions }
    });
  }

  async updateStepProgress(request: StepProgressRequest): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      // Handle encryption for sensitive data
      let encryptedData = undefined;
      if (request.data && (request.data.questions || request.data.notes || request.data.reflections)) {
        try {
          const dataToEncrypt = {
            questions: request.data.questions,
            notes: request.data.notes,
            reflections: request.data.reflections
          };
          
          encryptedData = await EncryptionService.encrypt(dataToEncrypt);
          
          // Data encrypted successfully
        } catch (encryptionError) {
          console.error('❌ Encryption failed, saving without encryption:', encryptionError);
          // Fall back to saving without encryption
          encryptedData = undefined;
        }
      } else {
        // No sensitive data to encrypt
      }
      
      // Prepare the request with encrypted data
      const requestWithEncryption = {
        ...request,
        data: {
          ...request.data,
          encryptedData: encryptedData,
          encrypted: !!encryptedData
        }
      };

      const response = await fetch(`${API_BASE_URL}/progress/step`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestWithEncryption)
      });

      if (!response.ok) {
        throw new Error(`Failed to update step progress: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating step progress:', error);
      throw error;
    }
  }

  async updateStepDates(stepNumber: number, dates: { started_at?: string; completed_at?: string }): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${API_BASE_URL}/progress/step/${stepNumber}/dates`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(dates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update step dates: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating step dates:', error);
      throw error;
    }
  }

  async updateActionProgress(request: { program: string; track: string; step_number: number; action_id: number; completed: boolean }): Promise<any> {
    return this.updateStepProgress({
      program: request.program,
      track: request.track,
      step_number: request.step_number,
      step_title: `Step ${request.step_number}`,
      completed: request.completed,
      data: { action_id: request.action_id, completed: request.completed }
    });
  }

  async updateStatus(request: { program: string; track: string; status_note: string; progress_milestone?: string }): Promise<any> {
    // Status updates are not used in the new structure
    return Promise.resolve({ message: 'Status update skipped' });
  }

  async getJourneyProgress(): Promise<JourneyProgress> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${API_BASE_URL}/progress/journey`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to get journey progress: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting journey progress:', error);
      throw error;
    }
  }

  async updateDateTrackingPreference(optedOut: boolean): Promise<any> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${API_BASE_URL}/user/preferences/date-tracking`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ date_display_opted_out: optedOut })
      });

      if (!response.ok) {
        throw new Error(`Failed to update date tracking preference: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating date tracking preference:', error);
      throw error;
    }
  }

  async getProgressHistory(): Promise<{
    programs: Record<string, {
      tracks: Record<string, {
        steps: Record<number, {
          lastCompletedAt: string | null;
          totalCompletions: number;
        }>;
      }>;
    }>;
  }> {
    const allProgress = await this.getUserProgress();
    const history: {
      programs: Record<string, {
        tracks: Record<string, {
          steps: Record<number, {
            lastCompletedAt: string | null;
            totalCompletions: number;
          }>;
        }>;
      }>;
    } = {
      programs: {}
    };
    allProgress.forEach(item => {
      const { program, track, step_number, completed, completed_at } = item;
      if (!program || !track) return;
      if (!history.programs[program]) {
        history.programs[program] = { tracks: {} };
      }
      if (!history.programs[program].tracks[track]) {
        history.programs[program].tracks[track] = {
          steps: {}
        };
      }
      if (step_number && completed && completed_at) {
        if (!history.programs[program].tracks[track].steps[step_number]) {
          history.programs[program].tracks[track].steps[step_number] = {
            lastCompletedAt: null,
            totalCompletions: 0
          };
        }
        const stepData = history.programs[program].tracks[track].steps[step_number];
        stepData.totalCompletions++;
        if (!stepData.lastCompletedAt || new Date(completed_at) > new Date(stepData.lastCompletedAt)) {
          stepData.lastCompletedAt = completed_at;
        }
      }
    });
    return history;
  }
}

export const progressService = new ProgressService(); 